import classNames from 'classnames';
import dayjs from 'dayjs';
import React from 'react';
import { AssetTypeField } from '../../../utils/api/asset';
import { ProjectAsset } from '../../../utils/api/projectAsset';

/** The asset type's active inspectable fields — the per-inspection checks. */
export const inspectableFieldsOf = (asset: ProjectAsset) =>
  (asset.asset_type?.fields || []).filter((f) => f.is_inspectable && f.is_active);

/** Required checks with no answer for this inspection; empty = checks complete. */
export const unansweredChecks = (asset: ProjectAsset) =>
  inspectableFieldsOf(asset)
    .filter((f) => f.is_required)
    .filter(
      (f) =>
        !asset.inspection_answers
          ?.find((a) => a.asset_field_type_id === f.id)
          ?.value?.trim(),
    );

/** "Archived on MM/DD/YYYY" for an asset archived after this inspection, else null. */
export const archivedOnLabel = (asset: ProjectAsset) =>
  asset.archived_at && !asset.archived_for_inspection
    ? `Archived on ${dayjs(asset.archived_at).format('MM/DD/YYYY')}`
    : null;

/** Confirmation text before a removal status archives the asset. */
export const removalWarning = (asset: ProjectAsset) =>
  `This will archive ${asset.asset_type?.code}-${asset.asset_name}. It will stay on inspections dated before today.`;

/** Fixed choices for a field, or null when it is free text/number/date. */
export const checkOptions = (f: AssetTypeField) => {
  if (f.field_type === 'select') return f.metadata?.options || [];
  if (f.field_type === 'yes_no') return ['Yes', 'No'];
  if (f.field_type === 'yes_no_na') return ['Yes', 'No', 'N/A'];

  return null;
};

interface IAssetChecksProps {
  fields: AssetTypeField[];
  valueOf: (fieldId?: number) => string;
  onSave: (field: AssetTypeField, value: string) => void;
  canEdit: boolean;
}

/** Per-inspection checks: option fields render as button groups, the rest as inputs. */
const AssetChecks: React.FC<IAssetChecksProps> = ({
  fields,
  valueOf,
  onSave,
  canEdit,
}) => (
  <div className='asset-section__row'>
    <h5>Inspection Checks</h5>
    <div className='asset-section__checks'>
      {fields.map((field) => {
        const options = checkOptions(field);
        const value = valueOf(field.id);

        return (
          <label key={field.id} className='asset-section__check'>
            <span>{field.label}{field.is_required && <span>*</span>}</span>
            {options ? (
              <div className='asset-section__check-btns'>
                {options.map((o) => (
                  <button
                    key={o}
                    type='button'
                    className={classNames({ 'is-active': value === o })}
                    disabled={!canEdit}
                    onClick={() => onSave(field, o)}
                  >
                    {o}
                  </button>
                ))}
              </div>
            ) : (
              <input
                type={
                  field.field_type === 'number'
                    ? 'number'
                    : field.field_type === 'date'
                      ? 'date'
                      : 'text'
                }
                defaultValue={value}
                disabled={!canEdit}
                placeholder={
                  field.field_type === 'number'
                    ? `in ${field.metadata?.unit}` || undefined
                    : undefined
                }
                onBlur={(e) => onSave(field, e.target.value)}
              />
            )}
          </label>
        );
      })}
    </div>
  </div>
);

export default AssetChecks;
